import { useEffect, useMemo, useState } from "react";
import Button from "app/storybookComponents/Button";
import SimpleModal from "../Modals/SimpleModal";
import WeeklyCheckInWhyIllustration from "resources/images/WeeklyCheckInWhyIllustration.png";
import WeeklyCheckInWhatToExpectIllustration from "resources/images/WeeklyCheckInWhatToExpectIllustration.png";
import WeeklyCheckInHowItWorksIllustration from "resources/images/WeeklyCheckInHowItWorksIllustration.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DotNavigator from "./DotNavigator";
import {
  resetChatbotState,
  selectThreadId,
  setChatbotFullScreen,
  setSampleChatState,
  setThreadId,
} from "app/containers/Chatbot/slice";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import { setSampleWeeklyCheckInEmailReportModalState } from "app/containers/WeeklyCheckInMonitor/slice";
import { useNavigate } from "react-router-dom";
import SampleWeeklyCheckInEmailReportsModal from "./SampleComponents/SampleWeeklyCheckInEmailReportsModal";
import { CheckInEmailReportType } from "app/containers/WeeklyCheckInMonitor/types";
import { ModalSection, WeeklyCheckInMerchandisingType } from "./types";
import { getWeeklyCheckInMerchandisingContent } from "./helpers";
import { WEEKLY_CHECK_IN_FEATURE_NAME } from "app/containers/WeeklyCheckInMonitor/constants";

interface Props {
  show?: boolean;
  onHide: () => void;
  hidePilotButton?: boolean;
  type: WeeklyCheckInMerchandisingType;
}

const WeeklyCheckInWelcomeModal = ({
  show,
  onHide,
  hidePilotButton,
  type,
}: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const threadId = useAppSelector(selectThreadId);

  const [activeStep, setActiveStep] = useState<number>(1); // 1 = Welcome, 2 = Preview, 3 = Learn More
  const [isPreviewReportModalOpen, setIsPreviewReportModalOpen] = useState<{
    isOpen: boolean;
    type?: CheckInEmailReportType | "sample-weekly-check-in-chat";
  }>({
    isOpen: false,
  });

  const SELECTED_TEMPLATE = useMemo(
    () => getWeeklyCheckInMerchandisingContent(type),
    [type]
  );

  useEffect(() => {
    if (!show) {
      setActiveStep(1);
    } else {
      dispatch(setChatbotFullScreen(true));
    }
  }, [show, dispatch]);

  const onCloseWrapper = () => {
    onHide();
    setIsPreviewReportModalOpen({ isOpen: false });
    if (threadId) {
      dispatch(setThreadId(null));
      dispatch(resetChatbotState());
      dispatch(setSampleChatState({ isSampleChatShowing: false }));
    }
    dispatch(
      setSampleWeeklyCheckInEmailReportModalState({
        type: "sample-weekly-check-in-chat",
      })
    );
  };

  const handleNextStep = () => {
    if (activeStep === 3) {
      return; // Cannot go past step 3
    }

    setActiveStep((prevStep) => prevStep + 1);
  };

  const handlePreviousStep = () => {
    if (activeStep === 1) {
      return; // Cannot go before step 1
    }

    setActiveStep((prevStep) => prevStep - 1);
  };

  const onStartPilot = () => {
    navigate("/AdminConsole/Organization%20Settings/Check-Ins");
    onCloseWrapper();
  };

  const getModalContent = () => {
    let imgSrc: string | null = null;
    let modalSection: ModalSection | null = null;
    switch (activeStep) {
      case 2:
        imgSrc = WeeklyCheckInWhyIllustration;
        modalSection = SELECTED_TEMPLATE.whyYouNeedIt;
        break;
      case 3:
        imgSrc = WeeklyCheckInWhatToExpectIllustration;
        modalSection = SELECTED_TEMPLATE.whatToExpect;
        break;
      case 1:
      default:
        imgSrc = WeeklyCheckInHowItWorksIllustration;
        modalSection = SELECTED_TEMPLATE.howItWorks;
        break;
    }

    if (!imgSrc || !modalSection) {
      return null;
    }

    return (
      <div className="weekly-check-in-welcome-modal-container">
        <div className="weekly-check-in-welcome-modal-container__illustration">
          <img
            src={imgSrc}
            alt={WEEKLY_CHECK_IN_FEATURE_NAME}
            className="weekly-check-in-welcome-modal-container__illustration__img"
          />
        </div>
        <div className="weekly-check-in-welcome-modal-container__content">
          <h1>{modalSection.title}</h1>
          <ul className="weekly-check-in-welcome-modal-container__content__ul">
            {modalSection.bullets.map((bullet) => (
              <li key={bullet}>{bullet}</li>
            ))}
          </ul>
          <div className="row-gap-4px">
            <span className="quote-start-large">“</span>
            <div className="column-gap-8px grey-text">
              <p>{modalSection.quote.text}</p>
              <p>
                <b>{modalSection.quote.author}</b>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getLeftMostButton = () => {
    if (activeStep < 2) {
      return (
        <div className="weekly-check-in-welcome-modal__footer__leftmost-buttons" />
      );
    }
    return (
      <div className="weekly-check-in-welcome-modal__footer__leftmost-buttons">
        <Button variant="secondary-blue" onClick={handlePreviousStep}>
          <FontAwesomeIcon icon="chevron-left" />
        </Button>
      </div>
    );
  };

  const getRightMostButton = () => {
    if (activeStep !== 3) {
      return (
        <div className="weekly-check-in-welcome-modal__footer__rightmost-buttons">
          <Button
            variant="secondary-blue"
            className="white-button"
            onClick={() => {
              setIsPreviewReportModalOpen({
                isOpen: true,
                type: "check-in-email",
              });
            }}
          >
            Preview experience
          </Button>
          <Button onClick={handleNextStep}>
            <FontAwesomeIcon icon="chevron-right" />
          </Button>
        </div>
      );
    }

    if (type !== "admin") {
      return (
        <div className="weekly-check-in-welcome-modal__footer__rightmost-buttons">
          <Button
            variant="secondary-blue"
            className="white-button"
            onClick={() => {
              setIsPreviewReportModalOpen({
                isOpen: true,
                type: "check-in-email",
              });
            }}
          >
            Preview experience
          </Button>
        </div>
      );
    }

    return (
      <div className="weekly-check-in-welcome-modal__footer__rightmost-buttons">
        {/* <Button
          variant="secondary-blue"
          className="white-button"
          onClick={onContactUs}
        >
          Contact us
        </Button> */}
        {hidePilotButton ? null : (
          <Button onClick={onStartPilot}>Start pilot</Button>
        )}
      </div>
    );
  };

  const getModalActionButtons = () => {
    return (
      <div className="weekly-check-in-welcome-modal__footer">
        {getLeftMostButton()}
        <DotNavigator
          activeStep={activeStep - 1}
          totalSteps={3}
          onStepChange={(step) => setActiveStep(step + 1)}
          size="small"
        />
        {getRightMostButton()}
      </div>
    );
  };

  return (
    <>
      <SimpleModal
        show={show}
        size="xl"
        onHide={onCloseWrapper}
        title="Learn more about Manager Check-ins"
        className="weekly-check-in-welcome-modal"
        centered
      >
        {getModalContent()}
        {getModalActionButtons()}
      </SimpleModal>
      <SampleWeeklyCheckInEmailReportsModal
        isOpen={isPreviewReportModalOpen.isOpen}
        onModalClose={() => setIsPreviewReportModalOpen({ isOpen: false })}
        type={isPreviewReportModalOpen.type}
        backdrop={false}
        onTypeChange={(type) =>
          setIsPreviewReportModalOpen({ isOpen: true, type })
        }
        closeButtonText="Back"
      />
    </>
  );
};

export default WeeklyCheckInWelcomeModal;
